import type { LiveBillResponse } from '@monorepo/shared'
import { RefreshCw, Zap } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useEnvironment } from '@/features/environments/contexts/EnvironmentContext'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Progress } from '@/shared/components/ui/progress'
import { LIVE_BILL_POLL_MS } from '@/shared/constants/polling'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { customerDetailService } from '../../services/customerDetailService'
import type { CustomerResponse } from '../../services/customerService'

interface LiveBillTabProps {
  customer: CustomerResponse
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)

const fmtQty = (n: number) => {
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1)}M`
  }
  if (n >= 1_000) {
    return `${(n / 1_000).toFixed(1)}K`
  }
  return n.toLocaleString()
}

export function LiveBillTab({ customer }: LiveBillTabProps) {
  const { currentEnvironment } = useEnvironment()
  const [data, setData] = useState<LiveBillResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date())

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch when environment changes
  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await customerDetailService.getLiveBill(customer.id)
      setData(result)
      setLastRefreshed(new Date())
    } catch (err) {
      setError('Failed to load billing data')
      toast.error(extractErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [customer.id, currentEnvironment?.id])

  useEffect(() => {
    load()
    // Auto-refresh every 30s
    const interval = setInterval(load, LIVE_BILL_POLL_MS)
    return () => clearInterval(interval)
  }, [load])

  if (loading && !data) {
    return (
      <div className='space-y-4'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {[0, 1].map(i => (
            <Card key={i} className='bg-transparent border'>
              <CardContent className='p-6'>
                <div className='animate-pulse space-y-3'>
                  <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-32' />
                  <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-24' />
                  <div className='h-2 bg-slate-300 dark:bg-slate-600 rounded' />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className='bg-transparent border'>
          <CardContent className='p-6'>
            <div className='animate-pulse space-y-3'>
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  className='h-6 bg-slate-300 dark:bg-slate-600 rounded'
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <Card className='bg-transparent border'>
        <CardContent className='text-center py-12'>
          <p className='text-red-500 mb-4'>{error}</p>
          <Button variant='outline' size='sm' onClick={load}>
            <RefreshCw className='w-4 h-4 mr-2' />
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return null
  }

  const { period, summary, line_items } = data

  const periodLabel = `${new Date(period.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${new Date(period.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (Today)`

  const resetDate = new Date(period.reset_date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <div>
          <h2 className='text-xl font-semibold'>Current Bill</h2>
          <p className='text-sm text-muted-foreground mt-1'>
            Updated {lastRefreshed.toLocaleTimeString()} · auto-refreshes every
            30s
          </p>
        </div>
        <Button variant='outline' size='sm' onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {/* Included Usage */}
        <Card className='bg-transparent border'>
          <CardContent className='p-6 space-y-3'>
            <p className='text-sm text-muted-foreground'>Your included usage</p>
            <p className='text-3xl font-bold'>
              {fmt(summary.included_used_usd)}
              <span className='text-lg font-normal text-muted-foreground'>
                {' '}
                / {fmt(summary.included_total_usd)}
              </span>
            </p>
            <Progress value={summary.included_pct} className='h-1.5' />
            <p className='text-xs text-muted-foreground'>Resets {resetDate}</p>
          </CardContent>
        </Card>

        {/* On-Demand */}
        <Card className='bg-transparent border'>
          <CardContent className='p-6 space-y-3'>
            <p className='text-sm text-muted-foreground'>On-Demand Usage</p>
            <p className='text-3xl font-bold'>{fmt(summary.overage_usd)}</p>
            <p className='text-sm text-muted-foreground'>
              Pay for extra usage beyond your plan limits.
            </p>
            {!data.has_contract && (
              <Badge variant='outline' className='text-xs'>
                No active contract
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Line Items */}
      <Card className='bg-transparent border'>
        <CardHeader>
          <CardTitle className='text-base'>
            Current Bill (Up to Today)
          </CardTitle>
          <CardDescription>Billing period: {periodLabel}</CardDescription>
          <div className='border-t mt-2' />
        </CardHeader>
        <CardContent className='space-y-1'>
          {line_items.length === 0 ? (
            <div className='text-center py-8'>
              <Zap className='w-8 h-8 mx-auto mb-3 text-muted-foreground/40' />
              <p className='text-sm text-muted-foreground'>
                No usage recorded this billing period.
              </p>
            </div>
          ) : (
            <>
              {line_items.map((item, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: stable order line items
                  key={i}
                  className='flex justify-between items-center py-2 text-sm'
                >
                  <div className='flex items-center gap-2'>
                    <span className='font-medium'>
                      {item.agent_name} – {item.signal_name}
                    </span>
                    <Badge variant='outline' className='text-xs'>
                      {item.action_type}
                    </Badge>
                    {item.is_overage && (
                      <Badge variant='secondary' className='text-xs'>
                        overage
                      </Badge>
                    )}
                  </div>
                  <div className='text-right'>
                    <div className='font-medium'>{fmt(item.revenue_usd)}</div>
                    <div className='text-xs text-muted-foreground'>
                      {fmtQty(item.quantity)} × ${item.unit_price_usd}
                    </div>
                  </div>
                </div>
              ))}

              <div className='border-t mt-4 pt-4 bg-muted/30 rounded-md p-4 space-y-1'>
                <div className='flex justify-between font-semibold'>
                  <span>Total (Current)</span>
                  <span>{fmt(summary.total_revenue_usd)}</span>
                </div>
                <div className='flex justify-between text-sm text-muted-foreground'>
                  <span>Estimated full month</span>
                  <span>{fmt(summary.projected_total_usd)}</span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
